import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../utils/api'

export const useProjectTasks = (projectId, options = {}) => {
  const [tasks, setTasks] = useState([])
  const [kanbanData, setKanbanData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  
  const { user } = useAuth()
  
  const {
    status = '',
    priority = '',
    assigned_to = '',
    search = '',
    autoFetch = true
  } = options

  // ดึงรายการงานย่อย
  const fetchTasks = async (page = pagination.page, limit = pagination.limit) => {
    if (!projectId) return
    
    try {
      setLoading(true)
      setError(null)
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(status && { status }),
        ...(priority && { priority }),
        ...(assigned_to && { assigned_to }),
        ...(search && { search })
      })
      
      const response = await api.get(`/api/projects/${projectId}/tasks?${params}`)
      
      if (response.data.success) {
        setTasks(response.data.data)
        setPagination(response.data.pagination)
      } else {
        throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูลงานย่อยได้')
      }
    } catch (err) {
      console.error('Error fetching tasks:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล')
    } finally {
      setLoading(false)
    }
  }

  // ดึงข้อมูล Kanban Board
  const fetchKanbanData = async () => {
    if (!projectId) return
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get(`/api/projects/${projectId}/tasks/kanban`)
      
      if (response.data.success) {
        setKanbanData(response.data.data)
      } else {
        throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูล Kanban Board ได้')
      }
    } catch (err) {
      console.error('Error fetching kanban data:', err)
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล Kanban Board')
    } finally {
      setLoading(false)
    }
  }

  // สร้างงานย่อยใหม่
  const createTask = async (taskData) => {
    if (!projectId) return { success: false, message: 'ไม่พบรหัสโครงการ' }
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.post(`/api/projects/${projectId}/tasks`, taskData)
      
      if (response.data.success) {
        // เพิ่มงานใหม่เข้าไปใน state
        setTasks(prev => [response.data.data, ...prev])
        
        // อัปเดต Kanban data
        await fetchKanbanData()
        
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถสร้างงานย่อยได้')
      }
    } catch (err) {
      console.error('Error creating task:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการสร้างงานย่อย'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // อัปเดตงานย่อย
  const updateTask = async (taskId, taskData) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.put(`/api/tasks/${taskId}`, taskData)
      
      if (response.data.success) {
        // อัปเดตใน tasks array
        setTasks(prev => prev.map(task => 
          task.id === taskId ? response.data.data : task
        ))
        
        // อัปเดต Kanban data
        await fetchKanbanData()
        
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถอัปเดตงานย่อยได้')
      }
    } catch (err) {
      console.error('Error updating task:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการอัปเดตงานย่อย'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // อัปเดตสถานะงาน (สำหรับ drag & drop)
  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.patch(`/api/tasks/${taskId}/status`, {
        status: newStatus
      })
      
      if (response.data.success) {
        // อัปเดตใน tasks array
        setTasks(prev => prev.map(task => 
          task.id === taskId ? { ...task, status: newStatus } : task
        ))
        
        // อัปเดต Kanban data
        await fetchKanbanData()
        
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถอัปเดตสถานะงานได้')
      }
    } catch (err) {
      console.error('Error updating task status:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการอัปเดตสถานะงาน'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // ลบงานย่อย
  const deleteTask = async (taskId) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.delete(`/api/tasks/${taskId}`)
      
      if (response.data.success) {
        // ลบออกจาก tasks array
        setTasks(prev => prev.filter(task => task.id !== taskId))
        
        // อัปเดต Kanban data
        await fetchKanbanData()
        
        return { success: true }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถลบงานย่อยได้')
      }
    } catch (err) {
      console.error('Error deleting task:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการลบงานย่อย'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // ดึงงานย่อยรายการเดียว
  const fetchTask = async (taskId) => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await api.get(`/api/tasks/${taskId}`)
      
      if (response.data.success) {
        return { success: true, data: response.data.data }
      } else {
        throw new Error(response.data.message || 'ไม่สามารถดึงข้อมูลงานย่อยได้')
      }
    } catch (err) {
      console.error('Error fetching task:', err)
      const errorMessage = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูลงานย่อย'
      setError(errorMessage)
      return { success: false, message: errorMessage }
    } finally {
      setLoading(false)
    }
  }

  // รีเฟรชข้อมูล
  const refreshTasks = () => {
    fetchTasks()
    fetchKanbanData()
  }

  // เปลี่ยนหน้า
  const changePage = (page, limit = pagination.limit) => {
    setPagination(prev => ({ ...prev, page, limit }))
    fetchTasks(page, limit)
  }

  // เปลี่ยนจำนวนรายการต่อหน้า
  const changeLimit = (limit) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }))
    fetchTasks(1, limit)
  }

  // ตั้งค่า filters
  const setFilters = (newFilters) => {
    fetchTasks(1, pagination.limit, newFilters)
  }

  // ล้าง filters
  const clearFilters = () => {
    fetchTasks(1, pagination.limit)
  }

  // ดึงข้อมูลอัตโนมัติเมื่อ projectId เปลี่ยน
  useEffect(() => {
    if (projectId && autoFetch) {
      fetchTasks()
      fetchKanbanData()
    }
  }, [projectId, autoFetch])

  return {
    // Data
    tasks,
    kanbanData,
    pagination,
    loading,
    error,
    
    // Methods
    fetchTasks,
    fetchKanbanData,
    createTask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    fetchTask,
    refreshTasks,
    changePage,
    changeLimit,
    setFilters,
    clearFilters,
    
    // Helpers
    canCreate: user?.permissions?.project_tasks?.create === true,
    canRead: user?.permissions?.project_tasks?.read === true,
    canUpdate: user?.permissions?.project_tasks?.update === true,
    canDelete: user?.permissions?.project_tasks?.delete === true
  }
}

export default useProjectTasks